import type { FC } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { MeinOrtBody } from '@/features/mein-ort/components/MeinOrtBody';
import { MeinOrtChannelSelector } from '@/features/mein-ort/components/MeinOrtChannelSelector';
import { MeinOrtCreatePostFab } from '@/features/mein-ort/components/MeinOrtCreatePostFab';
import { MeinOrtHeader } from '@/features/mein-ort/components/MeinOrtHeader';
import { MeinOrtLocationBar } from '@/features/mein-ort/components/MeinOrtLocationBar';
import { MeinOrtPageTabs } from '@/features/mein-ort/components/MeinOrtPageTabs';
import { MeinOrtScrollProvider } from '@/features/mein-ort/components/MeinOrtScrollContext';
import { MeinOrtSuggestionCreatePostFab } from '@/features/mein-ort/components/MeinOrtSuggestionCreatePostFab';
import { MeinOrtTabProvider } from '@/features/mein-ort/components/MeinOrtTabContext';
import type {
  MeinOrtChannel,
  MeinOrtTab
} from '@/features/mein-ort/components/meinOrtTypes';
import { useMeinOrtBadgeLifecycle } from '@/features/mein-ort/hooks/useMeinOrtBadgeLifecycle';
import { GradientBackground } from '@/shared/components/GradientBackground';
import { Expand } from '@/shared/components/layout/Expand';
import { isNative } from '@/shared/utils/platform';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props = {
  channel?: MeinOrtChannel;
  tab?: MeinOrtTab;
  onVisit: (params: { channel: MeinOrtChannel; tab: MeinOrtTab }) => void;
};

export const MeinOrtScreen: FC<Props> = ({ channel, tab, onVisit }) => {
  const theme = useAppTheme();
  useMeinOrtBadgeLifecycle();

  return (
    <MeinOrtScrollProvider>
      <MeinOrtTabProvider
        routeActiveChannel={channel}
        routeTab={tab}
        onVisit={onVisit}
      >
        <GradientBackground
          colors={[theme.colors.surface, theme.colors.secondaryContainer]}
          locations={[0.19, 0.84]}
          testID="mein-ort-screen"
        >
          <MeinOrtBackground />

          <MeinOrtHeader>
            <MeinOrtLocationBar />
            <View
              style={[
                styles.headerContent,
                { backgroundColor: theme.colors.surface }
              ]}
            >
              <MeinOrtChannelSelector />
              <MeinOrtPageTabs />
            </View>
          </MeinOrtHeader>
          <Expand>
            <MeinOrtBody />
          </Expand>

          <MeinOrtCreatePostFab />
          <MeinOrtSuggestionCreatePostFab />
        </GradientBackground>
      </MeinOrtTabProvider>
    </MeinOrtScrollProvider>
  );
};

const MeinOrtBackground: FC = () => {
  const insets = useSafeAreaInsets();
  const topInset = isNative ? insets.top : 0;
  const theme = useAppTheme();

  return (
    <View
      style={[
        styles.safeArea,
        { height: topInset, backgroundColor: theme.colors.surface },
        { pointerEvents: 'none' }
      ]}
    />
  );
};

const styles = StyleSheet.create({
  header: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 2
  },
  headerContent: {
    gap: 4,
    paddingTop: 4
  },
  safeArea: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 3
  }
});
